// ── Panel de plataforma — cuentas (2026-08-17) ───────────────────────────────
// Todo lo de acá pega contra /api/platform/*, que solo responde a
// is_platform_admin (403 real en el backend). Los componentes que usen estos
// hooks igual se montan detrás de la guarda de App.tsx — esa es cosmética, el
// 403 es el que protege.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { AccountModules, AccountStatus } from '@evo-hub/shared';
import { api } from '../lib/api.js';

export interface PlatformAccountRow {
  id: string;
  name: string;
  status: AccountStatus;
  subscriptionStatus: 'active' | 'grace_period' | 'suspended';
  billingExempt: boolean;
  /** Fin del período de prueba. null = nunca tuvo. Llega también vencida:
   *  "prueba vencida hace 12 días y no pagó" es a quién hay que llamar. */
  trialEndsAt: string | null;
  createdAt: string;
  lastPaymentAt: string | null;
  lastPaymentAmount: number | null;
  lastPaymentCurrency: string | null;
  nextChargeAt: string | null;
  /** Consumo BRUTO — todo lo llamado, sin descontar el cupo gratuito.
   *  null = no se pudo leer la DB de esa cuenta. La UI lo muestra como "—",
   *  NUNCA como $0: decir cero cuando no sabés es peor que decir que no sabés. */
  monthSpendUsd: number | null;
  /** Parte del bruto cubierta por el cupo gratuito compartido de Google. */
  monthCoveredUsd: number | null;
  /** Lo que se cobra de verdad. Es el número facturable — el bruto NO. */
  monthBillableUsd: number | null;
  monthLimitUsd: number | null;
  userCount: number;
  modules: AccountModules;
  hasActiveStripeSubscription: boolean;
}

export interface PlatformAccountUser {
  id: number;
  email: string;
  displayName: string | null;
  role: 'owner' | 'member';
  isPlatformAdmin: boolean;
  active: boolean;
  createdAt: string;
}

export interface PlatformAccountDetail extends PlatformAccountRow {
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  stripePriceId: string | null;
  gracePeriodStartedAt: string | null;
  usageByService: Array<{ service: string; quantity: number; costUsd: number }>;
  creditsByService: Array<{
    /** Llave de cupo: `servicio` o `servicio:feature` cuando el servicio tiene
     *  varios SKUs con cupos independientes. */
    service: string;
    /** Nombre legible del SKU. */
    label: string;
    grossQuantity: number;
    grossCostUsd: number;
    freeQuantity: number;
    coveredCostUsd: number;
    billableQuantity: number;
    billableCostUsd: number;
    /** null = este servicio no tiene cupo gratuito; se cobra entero. */
    tierQuantity: number | null;
    /** Consumo de TODAS las cuentas — el denominador del reparto. */
    platformQuantity: number;
  }>;
  usageMultiplier: number;
  usageBillingSince: string | null;
  usageCharges: Array<{ periodStart: string; periodEnd: string; amountCents: number; billableUsd: number; multiplier: number; stripeInvoiceItemId: string | null }>;
  users: PlatformAccountUser[];
}

export interface PlatformAccountPayments {
  hasCustomer: boolean;
  nextChargeAt: string | null;
  nextChargeAmount: number | null;
  nextChargeCurrency: string | null;
  paymentMethod: { brand: string; last4: string } | null;
  stripeSubscriptionStatus: string | null;
  cancelAtPeriodEnd: boolean;
  invoices: Array<{ id: string; date: string | null; amountPaid: number; currency: string; status: string | null; hostedInvoiceUrl: string | null }>;
}

const LIST_KEY = ['platform-accounts'] as const;

export function usePlatformAccounts(enabled = true) {
  return useQuery<{ accounts: PlatformAccountRow[] }>({
    queryKey: LIST_KEY,
    queryFn: () => api.get<{ accounts: PlatformAccountRow[] }>('/api/platform/accounts'),
    enabled,
  });
}

export function usePlatformAccount(accountId: string | undefined) {
  return useQuery<PlatformAccountDetail>({
    queryKey: ['platform-account', accountId],
    queryFn: () => api.get<PlatformAccountDetail>(`/api/platform/accounts/${accountId}`),
    enabled: !!accountId,
  });
}

/** Historial de Stripe — query aparte de usePlatformAccount a propósito: es la
 *  ÚNICA llamada live a Stripe del panel, y separarla deja que el detalle
 *  pinte de entrada mientras los pagos siguen cargando, en vez de que toda la
 *  pantalla espere a Stripe. */
export function usePlatformAccountPayments(accountId: string | undefined) {
  return useQuery<PlatformAccountPayments>({
    queryKey: ['platform-account-payments', accountId],
    queryFn: () => api.get<PlatformAccountPayments>(`/api/platform/accounts/${accountId}/payments`),
    enabled: !!accountId,
  });
}

// ── Consumo de API por mes (2026-10-05) ──────────────────────────────────────
// services/consumoMensual.ts. El mes en curso viene con cerrado:false y no se
// puede facturar; un mes cerrado sin cargo es lo que queda "por facturar".

export interface UsageMonth {
  /** 'YYYY-MM' */
  mes: string;
  cerrado: boolean;
  brutoUsd: number;
  cubiertoUsd: number;
  cobrableUsd: number;
  multiplicador: number;
  /** cobrable × multiplicador; si ya se facturó, lo facturado. */
  montoCents: number;
  porServicio: Array<{ servicio: string; etiqueta: string; cantidad: number; brutoUsd: number; cobrableUsd: number }>;
  cargo: {
    montoCents: number;
    cobrableUsd: number;
    multiplicador: number;
    registradoEn: string;
    enStripe: boolean;
    /** Lo cubrió el cobro automático por ciclo de Stripe, no este mes exacto. */
    porCiclo: boolean;
  } | null;
}

export function useAccountUsageMonths(accountId: string | undefined) {
  return useQuery<{ months: UsageMonth[] }>({
    queryKey: ['platform-account-usage-months', accountId],
    queryFn: () => api.get<{ months: UsageMonth[] }>(`/api/platform/accounts/${accountId}/usage-months`),
    enabled: !!accountId,
  });
}

export function useChargeUsageMonth() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, mes }: { id: string; mes: string }) =>
      api.post<{ mes: string; montoCents: number; descripcion: string; stripeInvoiceItemId: string }>(
        `/api/platform/accounts/${id}/usage-months/${mes}/charge`, {},
      ),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ['platform-account-usage-months', vars.id] });
      void qc.invalidateQueries({ queryKey: ['platform-account-payments', vars.id] });
      invalidateAccount(qc, vars.id);
    },
  });
}

// ── Puente de suscripción (2026-10-05) ───────────────────────────────────────
// Lo de la cuenta operadora lo ejecuta Claude Code en la Mac con el plan.

export interface PuenteEstado {
  encendido: boolean;
  /** Latió hace menos de 90 s y entra con un plan. */
  conectada: boolean;
  latido: { en: number; plan: string | null; version: string | null } | null;
  ultimoDia: { pendiente: number; tomado: number; listo: number; fallido: number; abandonado: number };
  ultimoError: { error: string; en: number } | null;
  cuentaOperadora: string;
}

export function usePuenteSuscripcion(enabled: boolean) {
  return useQuery<PuenteEstado>({
    queryKey: ['platform', 'puente'],
    queryFn: () => api.get<PuenteEstado>('/api/platform/puente'),
    enabled,
    refetchInterval: 30_000,
  });
}

export function useSetPuenteEncendido() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (encendido: boolean) => api.patch<{ encendido: boolean }>('/api/platform/puente', { encendido }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['platform', 'puente'] }); },
  });
}

function invalidateAccount(qc: ReturnType<typeof useQueryClient>, accountId: string) {
  void qc.invalidateQueries({ queryKey: LIST_KEY });
  void qc.invalidateQueries({ queryKey: ['platform-account', accountId] });
}

export interface StatusChangeResult {
  ok: boolean;
  status: AccountStatus;
  /** Cerrar la cuenta NO cancela el cobro (decisión de Eduardo). Cuando esto
   *  viene true, el panel tiene que avisarlo en el acto y ofrecer cancelar
   *  como un segundo paso explícito. */
  stripeSubscriptionStillActive: boolean;
}

export function useChangeAccountStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, confirmSlug }: { id: string; status: AccountStatus; confirmSlug?: string }) =>
      api.post<StatusChangeResult>(`/api/platform/accounts/${id}/status`, { status, confirmSlug }),
    onSuccess: (_data, vars) => invalidateAccount(qc, vars.id),
  });
}

export function useUpdateAccountLimits() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, monthlyLimitUsd, billingExempt, usageMultiplier, usageBillingSince, stripePriceId }: { id: string; monthlyLimitUsd?: number | null; billingExempt?: boolean; usageMultiplier?: number; usageBillingSince?: string | null; stripePriceId?: string | null }) =>
      api.patch<{ ok: boolean }>(`/api/platform/accounts/${id}/limits`, { monthlyLimitUsd, billingExempt, usageMultiplier, usageBillingSince, stripePriceId }),
    onSuccess: (_data, vars) => invalidateAccount(qc, vars.id),
  });
}

/** Concede la prueba en DÍAS desde hoy; `days: null` la quita. Días y no
 *  fecha absoluta: el panel no es un calendario y "30 días desde hoy" es lo
 *  que se decide en la práctica. */
export function useSetAccountTrial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, days }: { id: string; days: number | null }) =>
      api.patch<{ ok: boolean; trialEndsAt: string | null }>(`/api/platform/accounts/${id}/trial`, { days }),
    onSuccess: (_data, vars) => invalidateAccount(qc, vars.id),
  });
}

export interface ModulesUpdateResult {
  ok: boolean;
  modules: AccountModules;
  reEnabled: string[];
  reEnabledSince: Record<string, string | null>;
}

export function useUpdateAccountModules() {
  const qc = useQueryClient();
  return useMutation({
    // Estado COMPLETO, no parcial — el backend rechaza un objeto incompleto.
    mutationFn: ({ id, modules }: { id: string; modules: AccountModules }) =>
      api.put<ModulesUpdateResult>(`/api/platform/accounts/${id}/modules`, { modules }),
    onSuccess: (_data, vars) => invalidateAccount(qc, vars.id),
  });
}

export function useCancelAccountSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, confirmSlug, atPeriodEnd }: { id: string; confirmSlug: string; atPeriodEnd?: boolean }) =>
      api.post<{ ok: boolean; status: string; cancelAtPeriodEnd: boolean }>(
        `/api/platform/accounts/${id}/cancel-subscription`,
        { confirmSlug, atPeriodEnd },
      ),
    onSuccess: (_data, vars) => {
      invalidateAccount(qc, vars.id);
      void qc.invalidateQueries({ queryKey: ['platform-account-payments', vars.id] });
    },
  });
}

// ── Catálogo de planes (2026-08-19) ──────────────────────────────────────────
// Los planes se definen UNA VEZ a nivel plataforma. El monto NO viaja desde
// nuestra DB: lo resuelve el backend contra Stripe, así que puede venir null si
// Stripe no responde — la UI lo muestra como "—", nunca como cero.

export interface PlatformPlan {
  id: string;
  name: string;
  description: string | null;
  stripePriceId: string;
  active: boolean;
  sortOrder: number;
  amount: number | null;
  currency: string | null;
  interval: string | null;
}

export function usePlans() {
  return useQuery<{ plans: PlatformPlan[] }>({
    queryKey: ['platform', 'plans'],
    queryFn: () => api.get('/api/platform/plans'),
    staleTime: 10_000,
  });
}

export interface PlanInput {
  id?: string;
  name?: string;
  description?: string | null;
  stripePriceId?: string;
  active?: boolean;
  sortOrder?: number;
}

export function useCreatePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PlanInput) => api.post<{ plan: PlatformPlan }>('/api/platform/plans', input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['platform', 'plans'] }),
  });
}

export function useUpdatePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: PlanInput & { id: string }) =>
      api.patch<{ plan: PlatformPlan }>(`/api/platform/plans/${id}`, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['platform', 'plans'] }),
  });
}

export interface MigrationPreview {
  planId: string;
  planName: string;
  toPriceId: string;
  affected: Array<{ accountId: string; name: string; fromPriceId: string | null; effectiveFrom: string | null }>;
}

/** Paso 1 de la migración: a cuántos afecta y desde cuándo. No toca nada. */
export function usePlanMigrationPreview(planId: string | null) {
  return useQuery<MigrationPreview>({
    queryKey: ['platform', 'plans', planId, 'migration-preview'],
    queryFn: () => api.get(`/api/platform/plans/${planId}/migration-preview`),
    enabled: planId != null,
    staleTime: 0,   // siempre fresco: se decide sobre esta lista
  });
}

/** Paso 2: se mandan las cuentas EXACTAS que se vieron en el preview. */
export function useMigratePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ planId, accountIds }: { planId: string; accountIds: string[] }) =>
      api.post<{ migrated: string[]; failed: Array<{ accountId: string; error: string }> }>(
        `/api/platform/plans/${planId}/migrate`, { accountIds }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['platform'] }),
  });
}
